// Scripts for header section.
/**
 * Initializes hover and click behavior for a global navigation menu
 * with Bootstrap collapse-based mega menus and a backdrop.
 *
 * Features:
 * - Opens menus on hover with delay
 * - Supports manual toggle via button click
 * - Ensures only one menu is open at a time
 * - Closes menus when clicking outside (backdrop)
 *
 * Dependencies:
 * - Bootstrap Collapse (bootstrap.Collapse)
 *
 * @returns {void}
 */
export const globalMenuBackdrop = () => {
    // Main navigation container
    const globalmenu = document.getElementById('global-menu');
    if (!globalmenu) { return; }

    // All nav items that contain mega menus
    const items = globalmenu.querySelectorAll('.nav-item.has-mega');

    // Optional backdrop element for closing menus on outside click
    const backdrop = document.getElementById('backdrop');
    // milliseconds
    const hoverDelay = 300;
    // Timeout reference for delayed hover behavior
    let hoverTimeout = null;

    // Track last hovered item for sideways movement
    let lastHoveredItem = null;

    // Track the currently active/open menu
    let activeItem = null;

    // Track the item pinned open via click (distinct from hover-open state)
    // While this is set, hover is suppressed entirely across all items
    let pinnedItem = null;

    // Helper functions

    /**
     * Opens a Bootstrap collapse element (the mega menu)
     * @param {HTMLElement} collapseEl 
     */
    const openMenu = (collapseEl) => {
        const instance = bootstrap.Collapse.getOrCreateInstance(collapseEl, { toggle: false });
        instance.show();
    };

    /**
     * Closes a Bootstrap collapse element (the mega menu)
     * @param {HTMLElement} collapseEl 
     */
    const closeMenu = (collapseEl) => {
        const instance = bootstrap.Collapse.getOrCreateInstance(collapseEl, { toggle: false });
        instance.hide();
    };

    /**
     * Gets the toggle button for a given item.
     */
    const getBtn = (item) => item.querySelector('button');

    /**
     * Marks an item as expanded: syncs data-expanded, aria-expanded and Bootstrap collapse.
     * Single source of truth for "open" state, replacing the old separate handleDropdowns logic.
     * @param {HTMLElement} item 
     */
    const expandItem = (item) => {
        const collapseEl = item.querySelector('.collapse');
        const btn = getBtn(item);
        if (collapseEl) { openMenu(collapseEl); }
        if (btn) { btn.setAttribute('aria-expanded', 'true'); }
        item.dataset.expanded = 'true';
        activeItem = item;
    };

    /**
     * Marks an item as collapsed: syncs data-expanded, aria-expanded and Bootstrap collapse.
     * @param {HTMLElement} item 
     */
    const collapseItem = (item) => {
        const collapseEl = item.querySelector('.collapse');
        const btn = getBtn(item);
        if (collapseEl) { closeMenu(collapseEl); }
        if (btn) { btn.setAttribute('aria-expanded', 'false'); }
        item.dataset.expanded = 'false';
        if (activeItem === item) { activeItem = null; }
    };

    /**
     * Closes all open menus and resets state
     */
    const closeAllMenus = () => {
        items.forEach((item) => { collapseItem(item); });
        activeItem = null;
        pinnedItem = null;
        document.body.classList.remove('has-backdrop');
    };

    // Hover and click behavior
    items.forEach((item) => {
        const collapseEl = item.querySelector('.collapse');
        const btn = item.querySelector('button');

        if (!collapseEl) {
            return
        };

        // Handle mouse entering the menu item
        item.addEventListener('mouseenter', () => {
            // While a menu is pinned open via click, ignore hover entirely
            if (pinnedItem) { return; }

            clearTimeout(hoverTimeout); // cancel any pending hover actions

            // Determine if moving sideways from another menu item
            const isSideways = activeItem && activeItem !== item;
            lastHoveredItem = item;

            hoverTimeout = setTimeout(() => {
                if (isSideways) {
                    // Sideways hover: open new menu first
                    const previous = activeItem;
                    expandItem(item);
                    // Close previous menu after opening new one for smoothness
                    if (previous) { collapseItem(previous); }
                } else {
                    // Normal hover: close all first, then open
                    closeAllMenus();
                    expandItem(item);
                }
            }, isSideways ? 0 : hoverDelay); // instant if sideways, slight delay otherwise
        });

        // Handle mouse leaving the menu item
        item.addEventListener('mouseleave', (e) => {
            // While a menu is pinned open via click, ignore hover entirely
            if (pinnedItem) { return; }

            clearTimeout(hoverTimeout);

            // Only close if mouse leaves the entire menu area
            const relatedTarget = e.relatedTarget;
            if (!item.contains(relatedTarget) && !globalmenu.contains(relatedTarget)) {
                collapseItem(item);
            }
        });

        // Click toggle button for manual mega menu opening/closing
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const isPinned = pinnedItem === item;

            // Close every menu except the one we're about to open,
            // so we never collapse-then-immediately-expand the same element
            items.forEach((otherItem) => {
                if (otherItem !== item) { collapseItem(otherItem); }
            });
            activeItem = null;
            pinnedItem = null;
            document.body.classList.remove('has-backdrop');

            if (!isPinned) {
                expandItem(item);
                pinnedItem = item;
                document.body.classList.add('has-backdrop');
            } else {
                collapseItem(item);
            }
        });
    });

    // Backdrop click closes all mega menus
    if (backdrop) {
        backdrop.addEventListener('click', () => {
            closeAllMenus();
        });
    }

    // Clicking outside dropdowns closes all dropdowns and removes backdrop
    document.addEventListener('click', (event) => {
        if (!event.target.closest('.nav-item.has-mega')) {
            closeAllMenus();
        }
    });

    // Close dropdowns when tabbing out of them
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !activeItem) { return; }

        const subMenu = activeItem.querySelector('.drop-menu');
        const focusedElement = activeItem.querySelector(':focus');
        const focusable = [...subMenu.querySelectorAll('ul > li a')];
        if (!focusable.length) { return; }

        const firstFocusableElement = focusable[0];
        const lastFocusableElement = focusable[focusable.length - 1];

        if (!event.shiftKey && focusedElement === lastFocusableElement) {
            closeAllMenus();
        } else if (event.shiftKey && focusedElement === firstFocusableElement) {
            closeAllMenus();
        }
    });
};

/**
 * Initializes click and keyboard support for the local menu dropdowns.
 *
 * Features:
 * - Toggles `display: block` on the `.drop-menu` next to the clicked button
 * - Keeps `aria-expanded` on the button in sync
 * - Closes other open dropdowns when a new one opens
 * - Closes a dropdown when keyboard focus leaves its list item
 *
 * Uses event delegation on the navbar, so items cloned into the "Flere"
 * list by `handleOverflowingMenu` keep working. Call once on page load;
 * it does not need to be re-run on resize.
 *
 * Closing clears the inline style instead of setting `display: none`,
 * so the stylesheet's `:hover` rule keeps working for mouse users.
 *
 * @returns {void}
 */
export const localMenuToggles = () => {
    const menu = document.querySelector('.localmenu .navbar');
    if (!menu) { return; }

    const closeDrop = (drop) => {
        drop.style.display = '';   // hand control back to the stylesheet
        const btn = drop.previousElementSibling;
        if (btn && btn.hasAttribute('aria-expanded')) {
            btn.setAttribute('aria-expanded', 'false');
        }
    };

    // Delegated, so the clones inside "Flere" work too.
    menu.addEventListener('click', (e) => {
        const btn = e.target.closest('.nav-link-toggle, .toggler-icon');
        if (!btn) { return; }

        const drop = btn.nextElementSibling;
        if (!drop || !drop.classList.contains('drop-menu')) { return; }

        const open = drop.style.display !== 'block';

        // Close all other open menus, but not an ancestor of this one
        // (a clone inside "Flere" must not close "Flere" itself).
        menu.querySelectorAll('.drop-menu').forEach((other) => {
            if (other !== drop && !other.contains(btn)) { closeDrop(other); }
        });

        drop.style.display = open ? 'block' : '';
        btn.setAttribute('aria-expanded', String(open));
    });

    // Close when keyboard focus leaves the item.
    menu.addEventListener('focusout', (e) => {
        const li = e.target.closest('li');
        if (li && !li.contains(e.relatedTarget)) {
            li.querySelectorAll('.drop-menu').forEach(closeDrop);
        }
    });
};

/**
 * Initializes overflow handling for the horizontal local menu.
 *
 * When there isn't enough horizontal space to display all menu items,
 * the items that don't fit are moved (right to left) into a "More"
 * dropdown. The check runs once on init and again on every window
 * resize (throttled).
 *
 * Safe to call multiple times: a previously attached resize listener
 * from an earlier call is removed before a new one is added, so
 * repeated init (e.g. after an SPA route change) won't leak listeners.
 *
 * @returns {void}
 */
export const handleOverflowingMenu = () => {
    const nav = document.querySelector('.localmenu .navbar');
    if (isMobile() || !nav) {
        return;
    }

    const contentBar = nav.querySelector('.navbar-nav');
    const moreGroup = nav.querySelector('.navbar-nav > li.grouped');
    const navItems = [...nav.querySelectorAll('.navbar-nav > li:not(.grouped)')];

    // Bail out early if the expected "More" markup isn't present,
    // instead of throwing when we try to read moreList/moreGroup below.
    if (!moreGroup) {
        return;
    }

    const moreList = moreGroup.querySelector('ul.grouped-content');
    if (!moreList) {
        return;
    }

    /**
     * Measures available space and redistributes items between the
     * visible menu and the "More" dropdown accordingly.
     *
     * @returns {void}
     */
    const checkMenu = () => {
        nav.parentElement.classList.add('packed-menu');
        const availableWidth = nav.offsetWidth;

        // Reset dropdown contents.
        while (moreList.lastElementChild) {
            moreList.removeChild(moreList.lastElementChild);
        }

        // Show all items by default before re-measuring.
        navItems.forEach(item => item.classList.remove('d-none'));

        // Reset "More" state so a previous narrower-viewport state
        // (active highlight, visibility) doesn't persist once items
        // fit again.
        moreGroup.classList.remove('active');
        moreGroup.classList.add('d-none');

        for (let i = navItems.length; i-- > 0;) {
            // Right to left: move items into the "More" list until
            // the content bar fits within the available width.
            if (contentBar.offsetWidth > availableWidth) {
                const sourceAnchor = navItems[i].querySelector('a');

                const li = document.createElement('li');
                li.classList.add('nav-item');
                li.innerHTML = navItems[i].innerHTML;

                // Guard against items whose markup has no <a> child.
                const clonedAnchor = li.querySelector('a');
                if (clonedAnchor) {
                    clonedAnchor.classList.add('nav-link');
                }

                // Check the actual element's classes rather than
                // string-matching innerHTML, which breaks if class
                // order/content changes.
                if (sourceAnchor?.classList.contains('active')) {
                    moreGroup.classList.add('active');
                }
                if (sourceAnchor?.classList.contains('nav-link-toggle')) {
                    li.classList.add('has-children');
                }

                // Populate dropdown.
                moreList.prepend(li);
                navItems[i].classList.add('d-none');

                // Show "More" item since it now has content.
                moreGroup.classList.remove('d-none');
            } else {
                break;
            }
        }
    };

    checkMenu();

    // Remove any listener attached by a previous call to this function
    // before attaching a new one, to avoid stacking duplicate handlers.
    if (handleOverflowingMenu._resizeHandler) {
        window.removeEventListener('resize', handleOverflowingMenu._resizeHandler);
    }

    handleOverflowingMenu._resizeHandler = throttle(() => {
        checkMenu();
    }, 80);

    window.addEventListener('resize', handleOverflowingMenu._resizeHandler);
};

export const toggleGlobalMenu = () => {
    /**
     * Toggle global menu for mobile.
     */
    const trigger = document.querySelector('.logo-link');
    const collapseElement = document.getElementById('global-menu');

    // Top logo bar
    if (trigger && collapseElement) {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();

            if (collapseElement) {
                new bootstrap.Collapse(collapseElement);
            }
        });

        collapseElement.addEventListener('show.bs.collapse', event => {
            document.body.classList.add('global-open');
        });

        collapseElement.addEventListener('hidden.bs.collapse', event => {
            document.body.classList.remove('global-open');
        });
    }

    // Toggle sub menus
    const menuItemBtn = document.querySelectorAll('.has-mega .nav-link-toggle');

    if (menuItemBtn.length) {
        menuItemBtn.forEach((btn) => {
            const submenu = btn.nextElementSibling;

            if (!submenu) {
                return;
            }

            // Sync aria-expanded with Bootstrap events
            submenu.addEventListener('show.bs.collapse', () => {
                btn.setAttribute('aria-expanded', 'true');
            });

            submenu.addEventListener('hide.bs.collapse', () => {
                btn.setAttribute('aria-expanded', 'false');
            });

            btn.addEventListener('click', (e) => {
                e.preventDefault();

                /**
                 * Close other open submenu items
                 * before opening the current one.
                 */
                menuItemBtn.forEach((otherBtn) => {
                    if (otherBtn !== btn) {
                        const otherMenu = otherBtn.nextElementSibling;

                        if (
                            otherMenu &&
                            otherMenu.classList.contains('show')
                        ) {
                            bootstrap.Collapse.getOrCreateInstance(otherMenu).hide();
                        }
                    }
                });

                // Toggle current submenu
                bootstrap.Collapse.getOrCreateInstance(submenu).toggle();
            });
        });
    }
}